'use client'

import * as Sentry from '@sentry/nextjs'
import { MapPinOff, RefreshCw } from 'lucide-react'
import { Component, type ErrorInfo, type ReactNode } from 'react'
import { Button } from '@/components/ui/button'

/**
 * Error boundary for the RouteMap component.
 *
 * Why this is separate from a generic app-wide boundary:
 *
 * Mapbox GL JS runs a WebGL context inside the map canvas. That context
 * can be lost at any time for reasons completely outside our control —
 * mobile Safari reclaiming memory, the tab being backgrounded too long,
 * a driver-level GPU issue, a bad tile response. When the context is
 * lost and Mapbox throws on the next frame, React's default behavior is
 * to unmount the ENTIRE tree — meaning the user's Calculate button,
 * their results, and their share link all disappear. That's a broken
 * UX for what is actually a decorative failure: the calculation itself
 * ran fine, we just can't draw the route on a map.
 *
 * This boundary catches map-only failures and renders a graceful
 * "Map unavailable" panel with a retry. The rest of the estimator keeps
 * working — costs, warnings, share, PDF export all stay usable. On
 * retry we reset the boundary state and remount RouteMap, which
 * requests a fresh WebGL context.
 *
 * Sentry.captureException fires on every caught error so we actually
 * notice if this starts happening often.
 *
 * Why a class component in 2026: React has no functional equivalent
 * for componentDidCatch / getDerivedStateFromError. This is the one
 * component pattern where classes remain idiomatic.
 */

interface MapErrorBoundaryProps {
	children: ReactNode
	/** Unique key that resets the boundary when changed — e.g. a new route. */
	resetKey?: string | number
}

interface MapErrorBoundaryState {
	hasError: boolean
	errorMessage: string | null
}

export class MapErrorBoundary extends Component<MapErrorBoundaryProps, MapErrorBoundaryState> {
	state: MapErrorBoundaryState = { hasError: false, errorMessage: null }

	static getDerivedStateFromError(error: Error): MapErrorBoundaryState {
		return {
			hasError: true,
			errorMessage: error.message || 'The map component hit an unexpected error.',
		}
	}

	componentDidCatch(error: Error, errorInfo: ErrorInfo) {
		// Report to Sentry with a clear tag so we can slice map-specific
		// errors out of the general issue stream.
		Sentry.captureException(error, {
			tags: { component: 'RouteMap' },
			extra: { componentStack: errorInfo.componentStack },
		})
		console.error('[map-error-boundary] caught:', error.message)
	}

	componentDidUpdate(prevProps: MapErrorBoundaryProps) {
		// If the parent passes a new resetKey (e.g., user starts a new
		// calculation), automatically clear the boundary so the next
		// render attempts the map again. Saves the user a manual retry
		// click when they've already taken an action.
		if (
			this.state.hasError &&
			prevProps.resetKey !== this.props.resetKey &&
			this.props.resetKey !== undefined
		) {
			this.setState({ hasError: false, errorMessage: null })
		}
	}

	handleRetry = () => {
		this.setState({ hasError: false, errorMessage: null })
	}

	render() {
		if (!this.state.hasError) return this.props.children

		return (
			<div
				role="alert"
				className="flex h-full min-h-[300px] w-full flex-col items-center justify-center gap-3 rounded-lg border border-border bg-surface px-6 py-8 text-center"
			>
				<MapPinOff className="size-8 text-text-muted" aria-hidden />
				<div className="space-y-1">
					<p className="text-sm font-medium text-text-primary">Map unavailable</p>
					<p className="max-w-sm text-xs text-text-muted">
						Your route and fuel cost still calculated correctly — only the map visual failed to
						load. You can try again, or continue without it.
					</p>
				</div>
				<Button variant="outline" size="sm" onClick={this.handleRetry}>
					<RefreshCw className="size-3.5" />
					Retry map
				</Button>
			</div>
		)
	}
}
